import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import type { VideoItem } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const video = videos.find((v) => v.id === id) as VideoItem | undefined;
  const article = id ? articles[id] : undefined;

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <ScrollToTop />
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">未找到该视频</h1>
          <p className="text-sm text-foreground-500 mb-6">该视频可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-lg font-label font-medium transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatViews = (views: number): string => {
    if (views >= 10000) return (views / 10000).toFixed(1) + '万';
    return views.toString();
  };

  const relatedVideos = videos
    .filter((v) => v.id !== video.id && v.category === video.category)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <article>
          {/* Article Header */}
          <header className="mb-6 md:mb-8">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {video.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article?.author || '密桃视频'}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {video.releaseDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)} 次观看
              </span>
              <span className="flex items-center gap-1 text-primary-500">
                <i className="ri-links-line"></i>
                来源：{video.source}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image & Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={video.cover}
                  alt={video.title}
                  title={`${video.title} - 密桃视频免费在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400 mb-0.5">画质</p>
                  <p className="text-sm font-semibold text-foreground-800">{video.quality}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400 mb-0.5">时长</p>
                  <p className="text-sm font-semibold text-foreground-800">{video.duration}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400 mb-0.5">评分</p>
                  <p className="text-sm font-semibold text-primary-500">{video.rating}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400 mb-0.5">热度</p>
                  <p className="text-sm font-semibold text-accent-500">{formatViews(video.views)}</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              {/* Watch Button */}
              <div className="bg-background-100 rounded-lg p-6 mb-6 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer">
                  <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
                </div>
                <h2 className="text-base md:text-lg font-heading font-bold text-foreground-900 mb-1">
                  立即观看
                </h2>
                <p className="text-sm text-foreground-500">{video.title} - {video.quality} 高清画质</p>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h2 className="text-base font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
              </div>

              {/* Article Content */}
              {article && (
                <div className="prose-custom mb-6">
                  <h2 className="text-base font-heading font-bold text-foreground-900 mb-3">深度解读</h2>
                  <div
                    className="text-sm text-foreground-600 leading-relaxed space-y-3"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="pt-8 border-t border-background-200/70" aria-label="相关推荐">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-movie-line text-primary-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Navigation */}
        <nav className="mt-8 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4" aria-label="文章导航">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
          <Link
            to={`/category/${video.category}`}
            className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
          >
            查看更多同类视频
            <i className="ri-arrow-right-line"></i>
          </Link>
        </nav>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        image: video.cover,
        datePublished: video.releaseDate,
        dateModified: video.releaseDate,
        author: {
          '@type': 'Organization',
          name: video.source,
        },
        publisher: {
          '@type': 'Organization',
          name: '密桃视频',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.mitaovideo.com/article/${video.id}`,
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaovideo.com/' },
          { '@type': 'ListItem', position: 2, name: '文章', item: `https://www.mitaovideo.com/article/${video.id}` },
          { '@type': 'ListItem', position: 3, name: video.title },
        ],
      }) }} />
    </div>
  );
}